import type { PostHogConfig } from 'posthog-js';
import { isNoisePostHogException } from './errorNoise';

// Includes Supabase implicit-flow tokens, PKCE codes and email-link credentials.
// Never remove PostHog's top-level `token`: it is the public ingestion key.
export const AUTH_QUERY_PARAMETERS = [
  'access_token', 'refresh_token', 'provider_token', 'provider_refresh_token',
  'id_token', 'token', 'token_hash', 'code', 'authorization', 'api_key',
] as const;

const sensitiveKeys = new Set<string>(AUTH_QUERY_PARAMETERS);

/** Works on absolute/relative URLs and URLs embedded in captured element text. */
export function scrubAnalyticsString(value: string, depth = 0): string {
  return value.replace(/([?&#])([^=&#\s]+)=([^&#\s"'<>]*)/g, (match, separator: string, key: string, raw: string) => {
    try {
      if (sensitiveKeys.has(decodeURIComponent(key).toLowerCase())) {
        return `${separator}${key}=[REDACTED]`;
      }
      // Redirect URLs can contain another, percent-encoded authentication URL.
      if (depth < 3 && raw.includes('%')) {
        const decoded = decodeURIComponent(raw);
        const scrubbed = scrubAnalyticsString(decoded, depth + 1);
        if (scrubbed !== decoded) return `${separator}${key}=${encodeURIComponent(scrubbed)}`;
      }
    } catch {
      // Malformed escaping must not break analytics or the sign-in flow.
    }
    return match;
  });
}

/** Clone JSON-shaped telemetry; never mutate application objects or browser URLs. */
export function scrubAnalyticsPayload<T>(value: T): T {
  if (typeof value === 'string') return scrubAnalyticsString(value) as T;
  // The SDK supplies CaptureResult.timestamp as a Date, not an ISO string.
  if (value instanceof Date) return new Date(value.getTime()) as T;
  if (Array.isArray(value)) return value.map(scrubAnalyticsPayload) as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, scrubAnalyticsPayload(child)])) as T;
  }
  return value;
}

export const analyticsPrivacyConfig = {
  // Authentication uses URL fragments; the app does not use hash-based routing.
  // Native masking also covers replay metadata, heatmaps and initial-person URLs.
  disable_capture_url_hashes: true,
  mask_personal_data_properties: true,
  custom_personal_data_properties: [...AUTH_QUERY_PARAMETERS],
  before_send: (event) => {
    if (!event) return null;
    // Third-party and bot exceptions (link scanners, extensions, blocked
    // beacons) are not ours to fix; same classifier as the Sentry filter.
    if (event.event === '$exception' && isNoisePostHogException(event.properties)) return null;
    return scrubAnalyticsPayload(event);
  },
} satisfies Partial<PostHogConfig>;
